import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import ParentShell from './ParentShell';
import ChildPills from './ChildPills';
import ScoreRing from './ScoreRing';
import AddChildModal from './AddChildModal';
import Scroller, { ScrollCard, StudentTag } from './Scroller';
import { useParent } from './ParentContext';
import { P, cardStyle, btn } from './theme';
import { parsePercent } from './scoreUtils';

const QUICK_ACTIONS = [
  { to: '/parent/register', icon: '\u{1F4DD}', title: 'Register for Classes', desc: 'Find and book new sessions' },
  { to: '/parent/upcoming', icon: '\u{1F4C5}', title: 'Upcoming Classes', desc: 'View upcoming lessons' },
  { to: '/parent/one-on-one', icon: '\u{1F464}', title: '1:1 Classes', desc: 'Private tuition sessions' },
  { to: '/parent/homework', icon: '\u{1F4DA}', title: 'Homework', desc: 'View homework and answers' },
  { to: '/parent/mock-exams', icon: '\u{1F4C4}', title: 'Mock Exams', desc: 'Scores, feedback and papers' }
];

export default function ParentDashboard() {
  const { user } = useAuth();
  const { kids, selectedChild, loading: childrenLoading, reload: reloadChildren } = useParent();
  const [allUpcoming, setAllUpcoming] = useState([]); // every child's upcoming classes, tagged by child
  const [allFeedback, setAllFeedback] = useState([]); // every child's approved teacher feedback
  const [allHomework, setAllHomework] = useState([]); // every child's homework, newest first
  const [upcoming, setUpcoming] = useState([]);
  const [oneOnOne, setOneOnOne] = useState([]);
  const [attended, setAttended] = useState([]);
  const [mockExams, setMockExams] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/invoices').then(({ data }) => setInvoices(data));
  }, []);

  useEffect(() => {
    if (!kids.length) { setAllUpcoming([]); setAllFeedback([]); setAllHomework([]); return; }
    Promise.all(kids.map((k) => Promise.all([
      api.get(`/registrations/child/${k.id}/upcoming`),
      api.get(`/registrations/child/${k.id}/feedback`),
      api.get('/homework', { params: { child_id: k.id } }).catch(() => ({ data: [] }))
    ]).then(([u, f, h]) => ({
      upcoming: u.data.map((r) => ({ ...r, child_name: k.name })),
      feedback: f.data.map((r) => ({ ...r, child_name: k.name })),
      homework: h.data.map((r) => ({ ...r, child_name: k.name }))
    })))).then((results) => {
      setAllUpcoming(results.flatMap((r) => r.upcoming).sort((a, b) => new Date(a.timing) - new Date(b.timing)));
      setAllFeedback(results.flatMap((r) => r.feedback).sort((a, b) => new Date(b.timing) - new Date(a.timing)).slice(0, 12));
      setAllHomework(results.flatMap((r) => r.homework).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 12));
    });
  }, [kids]);

  useEffect(() => {
    if (!selectedChild) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get(`/registrations/child/${selectedChild.id}/upcoming`),
      api.get(`/one-on-one/child/${selectedChild.id}`),
      api.get(`/registrations/child/${selectedChild.id}/attended`),
      api.get(`/mock-exams/child/${selectedChild.id}`)
    ]).then(([u, o, a, m]) => {
      setUpcoming(u.data);
      setOneOnOne(o.data);
      setAttended(a.data);
      setMockExams(m.data);
      setLoading(false);
    });
  }, [selectedChild]);

  const outstanding = invoices.filter((i) => i.status === 'unpaid').reduce((s, i) => s + Number(i.total_amount), 0);

  const nextOneOnOne = oneOnOne.filter((o) => o.status === 'upcoming').sort((a, b) => new Date(a.timing) - new Date(b.timing))[0];
  const nextClass = [...upcoming.map((r) => ({ ...r, kind: 'class' })), ...(nextOneOnOne ? [{ ...nextOneOnOne, kind: '1:1' }] : [])]
    .sort((a, b) => new Date(a.timing) - new Date(b.timing))[0];

  const activity = [
    ...attended.map((r) => ({ date: r.timing, title: r.title, sub: `Class attended · Teacher: ${r.teacher_name}` })),
    ...oneOnOne.filter((o) => o.status === 'completed').map((o) => ({ date: o.timing, title: o.topic || '1:1 Session', sub: `Completed · Teacher: ${o.teacher_name}` })),
    ...mockExams.map((m) => ({ date: m.exam_date, title: m.exam_name, sub: m.score ? `Score: ${m.score}` : 'Mock exam recorded' }))
  ].filter((a) => a.date).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  const latestMockExam = [...mockExams].sort((a, b) => new Date(b.exam_date) - new Date(a.exam_date))[0];
  const latestPercent = latestMockExam ? parsePercent(latestMockExam.score) : null;

  return (
    <ParentShell>
      <div style={{ marginBottom: 4, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: P.gold, fontWeight: 700 }}>Parent Portal</div>
      <h1 style={{ fontFamily: P.headlineFont, fontSize: 28, color: P.navy, margin: '0 0 6px' }}>
        Hello, {user?.name?.split(' ')[0] || 'there'}
      </h1>
      <p style={{ color: P.meta, marginBottom: 24 }}>
        {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        {selectedChild && ` · ${upcoming.length} class${upcoming.length === 1 ? '' : 'es'} booked for ${selectedChild.name}`}
      </p>

      <div style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: P.meta, fontWeight: 600, marginBottom: 8 }}>Your Children</div>
      <ChildPills onAddChild={() => setShowAddChild(true)} />

      {!childrenLoading && !selectedChild && (
        <div style={{ ...cardStyle, textAlign: 'center', color: P.meta }}>
          No children added yet. Use "+ Add a child" above to get started.
        </div>
      )}

      {selectedChild && (
        <>
          <Link to="/parent/invoices" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{
              ...cardStyle, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20,
              ...(outstanding > 0 ? { background: P.redWash, border: `1.5px solid ${P.red}`, borderLeft: `6px solid ${P.red}` } : {})
            }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: outstanding > 0 ? P.red : P.goldSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                {'\u{1F4CB}'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: P.navy }}>Invoices</div>
                <div style={{ fontSize: 12.5, color: P.meta }}>View invoices, payments and outstanding balances</div>
                {outstanding > 0 && (
                  <span style={{ display: 'inline-block', marginTop: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: P.red, padding: '4px 12px', borderRadius: 999 }}>
                    ⚠ £{outstanding.toFixed(2)} due
                  </span>
                )}
              </div>
              <span style={{ color: P.meta }}>{'›'}</span>
            </div>
          </Link>

          {allUpcoming.length > 0 && (
            <>
              <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 8 }}>Upcoming Classes</div>
              <Scroller>
                {allUpcoming.map((r) => (
                  <ScrollCard key={r.id}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: P.gold, textTransform: 'uppercase' }}>
                      {new Date(r.timing).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                    <div style={{ fontWeight: 600, color: P.navy, fontFamily: P.headlineFont, fontSize: 15, margin: '4px 0' }}>{r.title}</div>
                    <div style={{ fontSize: 12.5, color: P.meta, marginBottom: 8 }}>
                      {new Date(r.timing).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      {r.end_timing ? ` – ${new Date(r.end_timing).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </div>
                    <StudentTag name={r.child_name} />
                  </ScrollCard>
                ))}
              </Scroller>
            </>
          )}

          {allFeedback.length > 0 && (
            <>
              <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 8 }}>Latest Teacher Feedback</div>
              <Scroller>
                {allFeedback.map((f) => (
                  <ScrollCard key={f.id} accent={P.green}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                      <StudentTag name={f.child_name} />
                      {f.subject && <span style={{ fontSize: 10.5, fontWeight: 700, color: P.meta, textTransform: 'uppercase', letterSpacing: '.05em' }}>{f.subject}</span>}
                    </div>
                    <div style={{ fontSize: 12.5, color: P.navy, margin: '8px 0', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{f.content}</div>
                    <div style={{ fontSize: 11.5, color: P.meta }}>
                      {f.class_title} · {new Date(f.timing).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    </div>
                  </ScrollCard>
                ))}
              </Scroller>
            </>
          )}

          {allHomework.length > 0 && (
            <>
              <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 8 }}>Latest Homework</div>
              <Scroller>
                {allHomework.map((h) => (
                  <ScrollCard key={`${h.id}-${h.child_name}`} accent={P.navy}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: P.gold, textTransform: 'uppercase' }}>
                      Set {new Date(h.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    </div>
                    <div style={{ fontWeight: 600, color: P.navy, fontFamily: P.headlineFont, fontSize: 15, margin: '4px 0' }}>{h.title}</div>
                    <div style={{ fontSize: 12.5, color: P.meta, marginBottom: 8 }}>{h.class_title}</div>
                    <StudentTag name={h.child_name} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <a href={`/parent/view/${h.id}/homework`} target="_blank" rel="noopener noreferrer" style={{ ...btn('navy'), padding: '6px 12px', fontSize: 12 }}>View</a>
                      {h.has_answer && (
                        <a href={`/parent/view/${h.id}/answer`} target="_blank" rel="noopener noreferrer" style={{ ...btn('secondary'), padding: '6px 12px', fontSize: 12 }}>Answers</a>
                      )}
                    </div>
                  </ScrollCard>
                ))}
              </Scroller>
            </>
          )}

          <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 12 }}>Quick actions</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 24 }}>
            {QUICK_ACTIONS.map((qa) => (
              <Link key={qa.to} to={qa.to} style={{ textDecoration: 'none' }}>
                <div style={cardStyle}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{qa.icon}</div>
                  <div style={{ fontWeight: 600, color: P.navy, marginBottom: 3 }}>{qa.title}</div>
                  <div style={{ fontSize: 12.5, color: P.meta }}>{qa.desc}</div>
                </div>
              </Link>
            ))}
          </div>

          {!loading && nextClass && (
            <>
              <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 12 }}>Next Class</div>
              <div style={{ background: P.navy, borderRadius: P.radiusLg, padding: 20, marginBottom: 24, color: '#fff' }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{nextClass.title || nextClass.topic || (nextClass.kind === '1:1' ? '1:1 Session' : 'Class')}</div>
                <div style={{ fontSize: 13, color: '#C7CEDB' }}>{new Date(nextClass.timing).toLocaleString(undefined, { weekday: 'long', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</div>
                <div style={{ fontSize: 13, color: '#C7CEDB', marginTop: 2 }}>Teacher: {nextClass.teacher_name}</div>
              </div>
            </>
          )}

          {!loading && activity.length > 0 && (
            <>
              <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 12 }}>Recent Activity</div>
              <div style={{ ...cardStyle, padding: 0, marginBottom: 24 }}>
                {activity.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 18px', borderBottom: i < activity.length - 1 ? `1px solid ${P.line}` : 'none' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: P.gold, width: 60, flexShrink: 0 }}>
                      {new Date(a.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: P.navy }}>{a.title}</div>
                      <div style={{ fontSize: 12, color: P.meta }}>{a.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!loading && latestMockExam && (
            <>
              <div style={{ fontFamily: P.headlineFont, fontSize: 18, color: P.navy, marginBottom: 12 }}>Latest Mock Exam</div>
              <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 16 }}>
                {latestPercent !== null && <ScoreRing percent={latestPercent} />}
                <div>
                  <div style={{ fontWeight: 700, color: P.navy, fontFamily: P.headlineFont, fontSize: 16 }}>{latestMockExam.exam_name}</div>
                  <div style={{ fontSize: 12.5, color: P.meta }}>
                    {latestMockExam.exam_date ? new Date(latestMockExam.exam_date).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }) : 'Date TBD'}
                  </div>
                  {latestPercent === null && latestMockExam.score && <div style={{ fontSize: 13, color: P.navy, marginTop: 4 }}>Score: {latestMockExam.score}</div>}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {showAddChild && <AddChildModal onClose={() => setShowAddChild(false)} onAdded={() => { setShowAddChild(false); reloadChildren(); }} />}
    </ParentShell>
  );
}
